import React, { useState } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { LoadingSpinner, Button } from '@atoms';
import { SearchInput, ResourceFilter } from '@molecules';
import { PermissionTable, AdminPermissionModal } from '@organisms/admin';
import type { Permission } from '@organisms/admin';
import { useAuth } from '@contexts/AuthContext';
import toast from 'react-hot-toast';

export const AdminPermissionsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.isAdmin;

  const { 
    data: permissionsData, 
    isLoading, 
    error, 
    refetch 
  } = trpc.admin.getAllPermissions.useQuery();

  const deletePermissionMutation = trpc.admin.deletePermission.useMutation({
    onSuccess: () => {
      toast.success('Permission deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete permission');
    },
  });

  const permissions = permissionsData?.data || [];

  // Get unique resources for filter
  const uniqueResources = [...new Set(permissions.map(p => p.resource))].sort();

  // Filter permissions
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = 
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (permission.description && permission.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesResource = !resourceFilter || permission.resource === resourceFilter;

    return matchesSearch && matchesResource;
  });

  const handleCreatePermission = () => {
    setSelectedPermission(null);
    setIsEditing(false);
    setShowPermissionModal(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setSelectedPermission(permission);
    setIsEditing(true);
    setShowPermissionModal(true);
  };

  const handleDeletePermission = async (permission: Permission) => {
    if (window.confirm(`Are you sure you want to delete the permission "${permission.displayName}"?`)) {
      await deletePermissionMutation.mutateAsync({ id: permission.id });
    }
  };

  const handleModalClose = () => {
    setShowPermissionModal(false);
    setSelectedPermission(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading permissions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-error-500 mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Failed to load permissions
        </h3>
        <p className="text-text-secondary mb-4">
          {error.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Permission Management</h2>
          <p className="text-text-secondary mt-1">
            Manage system permissions and access controls
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={handleCreatePermission}
            variant="primary"
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Permission</span>
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search permissions..."
          className="flex-1 max-w-md"
        />
        <ResourceFilter
          resources={uniqueResources}
          value={resourceFilter}
          onChange={setResourceFilter}
          className="w-full sm:w-48"
        />
      </div>

      {/* Permissions Table */}
      <PermissionTable
        permissions={permissions}
        searchTerm={searchTerm}
        resourceFilter={resourceFilter}
        onEditPermission={isSuperAdmin ? handleEditPermission : undefined}
        onDeletePermission={isSuperAdmin ? handleDeletePermission : undefined}
        isDeleteDisabled={() => deletePermissionMutation.isPending}
        showActions={isSuperAdmin}
      />

      {/* Summary */}
      <div className="flex justify-between text-sm text-text-secondary">
        <span>
          Showing {filteredPermissions.length} of {permissions.length} permissions
        </span>
        <span>
          {permissions.filter(p => p.isActive).length} active, {permissions.filter(p => !p.isActive).length} inactive
        </span>
      </div>

      {/* Permission Modal */}
      {showPermissionModal && (
        <AdminPermissionModal
          permission={selectedPermission}
          isEditing={isEditing}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
